import axios from 'axios';
import { Request } from '../types/request';
import { Worker, Requester } from '../types/user';
import { Task } from '../types/task';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.usesieve.com';

class AdminService {
    async getRequests(): Promise<Request[]> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get<Request[]>(`${API_BASE_URL}/api/v1/admin/requests`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                withCredentials: true
            });

            return response.data;
        } catch (error: any) {
            console.error('Error fetching admin requests:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch requests');
        }
    }

    async getWorkerStats(): Promise<Worker[]> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get<Worker[]>(`${API_BASE_URL}/api/v1/admin/workers`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                withCredentials: true
            });

            return response.data; // No need for transformation, use data directly
        } catch (error: any) {
            console.error('Error fetching worker stats:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch worker statistics');
        }
    }

    async getRequesterStats(): Promise<Requester[]> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get<Requester[]>(`${API_BASE_URL}/api/v1/admin/requesters`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                withCredentials: true
            });

            return response.data; // No transformation needed anymore
        } catch (error: any) {
            console.error('Error fetching requester stats:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch requester statistics');
        }
    }

    async deleteRequests(requestIds: string[]) {
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('No authentication token found');
        }

        if (!requestIds.length) {
            throw new Error('No requests selected for deletion');
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/admin/jobs/delete_request`,
                { selected_jobs: requestIds },  // Changed from request_ids to selected_jobs
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true
                }
            );

            if (response.status === 200) {
                return true;
            } else {
                throw new Error(`Unexpected response status: ${response.status}`);
            }
        } catch (error: any) {
            console.error('Error deleting requests:', error);
            if (error.response?.status === 500) {
                throw new Error('Server error occurred while deleting requests. Please try again later.');
            }
            throw new Error(
                error.response?.data?.message || 
                error.message || 
                'Failed to delete requests'
            );
        }
    }

    async deactivateWorkers(workerIds: string[]) {
        const token = localStorage.getItem('authToken');
        if (!token) {
            throw new Error('No authentication token found');
        }

        if (!workerIds.length) {
            throw new Error('No workers selected for deactivation');
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/v1/admin/workers/deactivate`,
                { selected_users: workerIds }, // Changed to match backend expectation
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true
                }
            );

            if (response.status === 200) {
                return response.data;  // Return the actual response data
            }
            throw new Error(`Unexpected response status: ${response.status}`);
        } catch (error: any) {
            console.error('Error deactivating workers:', error);
            throw new Error(
                error.response?.data?.detail || // Use 'detail' field from FastAPI error response
                error.message || 
                'Failed to deactivate workers'
            );
        }
    }

    async deleteRequesters(requesterIds: string[]) {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.post(
                `${API_BASE_URL}/api/v1/admin/requesters/delete`,
                { user_ids: requesterIds },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true
                }
            );

            return response.data;
        } catch (error: any) {
            console.error('Error deleting requesters:', error);
            throw new Error(error.response?.data?.message || 'Failed to delete requesters');
        }
    }

    async getWorkerTasks(): Promise<any[]> {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(`${API_BASE_URL}/api/v1/admin/tasks`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                withCredentials: true
            });

            return response.data as Task[];
        } catch (error: any) {
            console.error('Error fetching worker tasks:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch worker tasks');
        }
    }
}

export const adminService = new AdminService();
